/**
 * WidgetBoard — rendert de widget-layout (CSS-grid, max 3 koloms) én de widget-
 * bibliotheek in één DnD-context, zodat je zowel kunt herordenen als nieuwe
 * widgets uit de bibliotheek het grid in kunt slepen.
 *
 * Herordenen gebeurt LIVE in onDragOver (de array verschuift, het grid reflowt
 * meteen) en zónder dnd-kit transforms — dat voorkomt het "funky" schalen van
 * buur-widgets bij variabele kolom-spans. Een DragOverlay toont een skeleton die
 * de cursor volgt. Schalen (breedte/hoogte) gaat via de knoppen in WidgetShell.
 */
import React, { useState } from 'react';
import { Box, Typography, Popover, useMediaQuery } from '@mui/material';
import {
  DndContext, DragOverlay, pointerWithin,
  PointerSensor, useSensor, useSensors, useDraggable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { T, Icon, MAX_COLS, GRID_GAP } from './widgetTokens';
import WidgetShell from './WidgetShell';
import WidgetSkeleton from './WidgetSkeleton';
import { WIDGETS, WIDGET_LIST, SECRET_WIDGETS } from './registry';
import { useSecretUnlock } from '../../../utils/secretUnlock';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const isPalette = (id) => typeof id === 'string' && id.startsWith('palette:');

function useResponsiveCols() {
  const isLg = useMediaQuery('(min-width:1200px)');
  const isMd = useMediaQuery('(min-width:680px)');
  return isLg ? MAX_COLS : isMd ? 2 : 1;
}

// ── Eén geplaatste widget (skeleton in edit-modus, echte inhoud daarbuiten) ──
function SortableWidget({ widget, cols, editMode, isDragging, onUpdate, onRemove }) {
  const meta = WIDGETS[widget.type];
  const [anchor, setAnchor] = useState(null);
  const { attributes, listeners, setNodeRef } = useSortable({
    id: widget.i, disabled: !editMode, animateLayoutChanges: () => false,
  });

  if (!meta) return null;
  const effW = clamp(widget.w, 1, cols);
  const settings = { ...(meta.defaultSettings || {}), ...(widget.settings || {}) };
  // Header-weergave kan afhangen van settings (bv. geheime game in 'today' = "Klok").
  const shellMeta = meta.displayFor ? { ...meta, ...(meta.displayFor(settings) || {}) } : meta;
  const Body = meta.Component;

  return (
    <Box ref={setNodeRef} style={{ gridColumn: `span ${effW}` }}>
      <WidgetShell
        meta={shellMeta}
        widget={{ ...widget, w: effW }}
        editMode={editMode}
        cols={cols}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
        hasSettings={!!meta.Settings}
        onSetWidth={(w) => onUpdate(widget.i, { w })}
        onSetHeight={(h) => onUpdate(widget.i, { h })}
        onRemove={() => onRemove(widget.i)}
        onOpenSettings={(el) => setAnchor(el)}
      >
        {editMode ? <WidgetSkeleton meta={meta} /> : (
          <Body widget={widget} settings={settings} editMode={false}
            onSettingsChange={(patch) => onUpdate(widget.i, { settings: { ...settings, ...patch } })} />
        )}
      </WidgetShell>

      {meta.Settings && (
        <Popover
          open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: '0.7rem', boxShadow: T.shadowHover, p: 2, minWidth: 240 } }}
        >
          <meta.Settings
            settings={settings}
            onChange={(patch) => onUpdate(widget.i, { settings: { ...settings, ...patch } })}
          />
        </Popover>
      )}
    </Box>
  );
}

// ── Insertie-plek bij het slepen vanuit de bibliotheek ──
function InsertPlaceholder({ type, cols }) {
  const meta = WIDGETS[type];
  if (!meta) return null;
  const span = clamp(meta.defaultW, 1, cols);
  return (
    <Box sx={{ gridColumn: `span ${span}`, minHeight: 96 }}>
      <Box sx={{
        height: '100%', minHeight: 96, borderRadius: '0.9rem',
        border: `2px dashed ${meta.accent.color}`, bgcolor: `${meta.accent.bg}55`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
        color: meta.accent.color, fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem',
      }}>
        <Icon name="add" size={18} /> {meta.title}
      </Box>
    </Box>
  );
}

// ── Bibliotheek-item (klikbaar = toevoegen aan eind; sleepbaar = plaatsen) ──
function LibraryItem({ meta, onAdd }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `palette:${meta.type}` });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onAdd(meta.type)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.75, py: 1.25, borderRadius: '0.7rem', cursor: 'grab', touchAction: 'none',
        bgcolor: T.surface, boxShadow: T.shadow, minWidth: 220,
        border: '1px solid transparent', transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
        opacity: isDragging ? 0.5 : 1,
        '&:hover': { borderColor: meta.accent.color, transform: 'translateY(-2px)', boxShadow: T.shadowHover },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{
        width: 34, height: 34, borderRadius: '0.55rem', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: meta.accent.bg, color: meta.accent.color,
      }}>
        <Icon name={meta.icon} size={20} fill />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.text, lineHeight: 1.2 }}>
          {meta.title}
        </Typography>
        {meta.description && (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {meta.description}
          </Typography>
        )}
      </Box>
      <Icon name="drag_indicator" size={18} sx={{ color: T.outlineVariant }} />
    </Box>
  );
}

function WidgetLibrary({ onAdd }) {
  const secretUnlocked = useSecretUnlock();
  return (
    <Box sx={{
      mt: 3, p: 2.5, borderRadius: '0.9rem',
      border: `1.5px dashed ${T.outlineVariant}`, bgcolor: 'rgba(82,96,117,0.025)',
    }}>
      <Typography sx={{
        fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, color: T.outline,
        textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5,
      }}>
        Widget-bibliotheek — sleep naar het dashboard of klik om toe te voegen
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {WIDGET_LIST.map((meta) => <LibraryItem key={meta.type} meta={meta} onAdd={onAdd} />)}
      </Box>

      {/* Geheime widgets — alleen zichtbaar binnen het unlock-venster van de code. */}
      {secretUnlocked && SECRET_WIDGETS.length > 0 && (
        <>
          <Typography sx={{
            fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 700, color: T.tertiary,
            textTransform: 'uppercase', letterSpacing: '0.08em', mt: 2.5, mb: 1.5,
            display: 'flex', alignItems: 'center', gap: 0.75,
          }}>
            <Icon name="lock_open" size={14} /> Geheime widgets
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {SECRET_WIDGETS.map((meta) => <LibraryItem key={meta.type} meta={meta} onAdd={onAdd} />)}
          </Box>
        </>
      )}
    </Box>
  );
}

function DragPreview({ meta }) {
  if (!meta) return null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5,
      bgcolor: T.surface, borderRadius: '0.9rem', boxShadow: T.shadowHover,
      transform: 'scale(1.02)', cursor: 'grabbing', minWidth: 200,
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '0.55rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: meta.accent.bg, color: meta.accent.color,
      }}>
        <Icon name={meta.icon} size={18} fill />
      </Box>
      <Box sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text }}>
        {meta.title}
      </Box>
    </Box>
  );
}

export default function WidgetBoard({ widgets, editMode, reorder, updateWidget, removeWidget, addWidgetAt }) {
  const cols = useResponsiveCols();
  const [activeId, setActiveId] = useState(null);     // herordenen
  const [paletteType, setPaletteType] = useState(null); // slepen vanuit bibliotheek
  const [overId, setOverId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const onDragStart = (e) => {
    const id = e.active.id;
    if (isPalette(id)) setPaletteType(id.slice('palette:'.length));
    else setActiveId(id);
  };

  const onDragOver = (e) => {
    const { active, over } = e;
    setOverId(over?.id ?? null);
    // Live herordenen van bestaande widgets (geen transforms → geen geschaal).
    if (activeId && over && active.id !== over.id && !isPalette(over.id)) {
      const oi = widgets.findIndex((w) => w.i === active.id);
      const ni = widgets.findIndex((w) => w.i === over.id);
      if (oi >= 0 && ni >= 0) reorder(arrayMove(widgets, oi, ni));
    }
  };

  const onDragEnd = (e) => {
    const { over } = e;
    if (paletteType) {
      let idx = widgets.length;
      if (over && !isPalette(over.id)) {
        const oi = widgets.findIndex((w) => w.i === over.id);
        if (oi >= 0) idx = oi;
      }
      addWidgetAt(paletteType, idx);
    }
    setActiveId(null); setPaletteType(null); setOverId(null);
  };

  const reset = () => { setActiveId(null); setPaletteType(null); setOverId(null); };

  // Insertie-index voor de placeholder tijdens bibliotheek-slepen.
  let insertIndex = null;
  if (paletteType) {
    insertIndex = widgets.length;
    if (overId && !isPalette(overId)) {
      const oi = widgets.findIndex((w) => w.i === overId);
      if (oi >= 0) insertIndex = oi;
    }
  }

  const overlayMeta = activeId
    ? WIDGETS[widgets.find((w) => w.i === activeId)?.type]
    : paletteType ? WIDGETS[paletteType] : null;

  return (
    <DndContext
      sensors={sensors}
      // Pointer-gebaseerd: de gesleepte widget staat stil (geen transforms), dus
      // closestCenter zou vanaf de oude positie meten. pointerWithin telt puur
      // waar de cursor staat → boven/onder plaatsen werkt overal over een widget.
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={widgets.map((w) => w.i)} strategy={rectSortingStrategy}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridAutoRows: 'minmax(0, auto)',
          alignItems: 'stretch',
          gap: `${GRID_GAP}px`,
        }}>
          {widgets.map((w, i) => (
            <React.Fragment key={w.i}>
              {paletteType && insertIndex === i && <InsertPlaceholder type={paletteType} cols={cols} />}
              <SortableWidget
                widget={w}
                cols={cols}
                editMode={editMode}
                isDragging={activeId === w.i}
                onUpdate={updateWidget}
                onRemove={removeWidget}
              />
            </React.Fragment>
          ))}
          {paletteType && insertIndex === widgets.length && <InsertPlaceholder type={paletteType} cols={cols} />}
        </Box>
      </SortableContext>

      {editMode && <WidgetLibrary onAdd={(type) => addWidgetAt(type, widgets.length)} />}

      <DragOverlay>{overlayMeta ? <DragPreview meta={overlayMeta} /> : null}</DragOverlay>
    </DndContext>
  );
}
